import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Scale,
  Network,
  CloudCog,
  FileCheck,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { services } from '../data/mock';
import ParallaxSection from '../components/graphics/ParallaxSection';
import FloatingElements from '../components/graphics/FloatingElements';
import SEO from '../components/SEO';

const iconMap = {
  ShieldCheck,
  Scale,
  Network,
  CloudCog,
  FileCheck
};

const ServicesPage = () => {
  const [hoveredService, setHoveredService] = useState(null);
  const location = useLocation();

  // Handle hash scroll on page load and hash change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  return (
    <div className="bg-slate-950 pt-20">
      <SEO 
        title="Services"
        description="Comprehensive cybersecurity and IT risk management services including GRC, TPRM, cloud security, and audit readiness for SOC 2, ISO 27001, and NIST compliance."
        keywords="cybersecurity services, GRC services, TPRM, vendor risk management, SOC 2 readiness, ISO 27001 consulting, NIST compliance, cloud security advisory"
        canonical="/services"
      />
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
          <FloatingElements />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Our Services</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6">
              Comprehensive Security Solutions
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              From risk assessments to audit readiness, we provide end-to-end cybersecurity and compliance services tailored to your organization.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <ParallaxSection className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;
              const isHovered = hoveredService === service.id;
              
              return (
                <div
                  key={service.id}
                  id={service.anchor}
                  className={`grid lg:grid-cols-2 gap-12 items-center scroll-mt-24 ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                  onMouseEnter={() => setHoveredService(service.id)}
                  onMouseLeave={() => setHoveredService(null)}
                >
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`relative w-14 h-14 transition-all duration-300 ${isHovered ? 'scale-110' : ''}`}>
                        <div className={`absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-teal-600/30 rounded-xl blur-lg transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-50'}`} />
                        <div className="relative w-full h-full bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center">
                          {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                        </div>
                      </div>
                      <h2 className={`text-2xl sm:text-3xl font-bold transition-colors duration-300 ${isHovered ? 'text-cyan-400' : 'text-white'}`}>
                        {service.category}
                      </h2>
                    </div>
                    
                    <ul className="space-y-4">
                      {service.items.map((item, itemIndex) => (
                        <li 
                          key={itemIndex} 
                          className="flex items-start gap-3 group/item"
                          style={{ animationDelay: `${itemIndex * 100}ms` }}
                        >
                          <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 transition-all duration-300 ${isHovered ? 'text-teal-400 scale-110' : 'text-cyan-400'}`} />
                          <span className="text-slate-300 group-hover/item:text-white transition-colors">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to="/contact" className="mt-8 inline-block">
                      <Button className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 group">
                        Get Started
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>

                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <Card className={`bg-slate-900/50 border-slate-800 overflow-hidden transition-all duration-500 ${isHovered ? 'border-cyan-500/50 scale-105' : ''}`}>
                      <CardContent className="p-0">
                        <div className="relative h-64 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden">
                          {/* Animated grid background */}
                          <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.05)_1px,transparent_1px)] bg-[size:20px_20px]" />
                          
                          {/* Animated rings */}
                          <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                            <div className="w-48 h-48 border border-cyan-500/20 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
                            <div className="absolute w-36 h-36 border border-teal-500/30 rounded-full animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />
                            <div className="absolute w-24 h-24 border border-cyan-500/40 rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '1s' }} />
                          </div>
                          
                          {/* Main icon */}
                          {IconComponent && (
                            <IconComponent className={`relative z-10 w-24 h-24 transition-all duration-500 ${isHovered ? 'text-cyan-400 scale-110' : 'text-cyan-500/20'}`} />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ParallaxSection>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Need a Custom Solution?
          </h2>
          <p className="text-slate-400 mb-10 max-w-2xl mx-auto">
            Every organization is unique. Let's discuss how we can tailor our services to meet your specific security and compliance needs.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300">
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
